import { ShippingDocumentKind } from "generated-graphql/graphql";
import { parseOmnisearchTokens } from "utils/omnisearchUtils";

/**
 * The Type filter's offered values: every value the Type column can print, so
 * a reader can filter to what they are looking at. A shipping document's kind
 * is whatever the user set in the edit dialog, which is why BILL_OF_LADING and
 * OTHER both belong here and not only the two the list's two sources suggest.
 *
 * Shipping documents are container-tracking data, and without that flag the
 * `shippingPaperwork` resolver restricts the list to the processed-manifest
 * source (see `shippingPaperwork.ts`'s `readableSources`). Every row such a
 * tenant can see is therefore a manifest, so offering Bill of Lading or Other
 * would offer a filter that can only ever empty the grid.
 */
export function shippingPaperworkKindValues(
  containerTrackingEnabled: boolean
): ShippingDocumentKind[] {
  if (containerTrackingEnabled) {
    return Object.values(ShippingDocumentKind);
  }
  return [ShippingDocumentKind.Manifest];
}

/**
 * Whether the current search can return manifests at all.
 *
 * The exports run the manifest-only `wasteManifests` query, which does not know
 * the `kind` key, so `toManifestExportSearch` strips it. That strips the only
 * term excluding manifests: filtering the grid to Bill of Lading left an export
 * that downloaded every manifest of the year from a grid showing none. The
 * export menu asks this first and offers nothing when the answer is no.
 *
 * A search carrying no `kind` term admits every kind, manifests included.
 */
export function searchIncludesManifests(search: string): boolean {
  const kindValues = parseOmnisearchTokens(search)
    .filter((token) => token.startsWith("kind:"))
    .flatMap((token) => token.slice("kind:".length).split(","))
    .map((value) => value.trim().toUpperCase())
    .filter(Boolean);

  if (kindValues.length === 0) return true;
  return kindValues.includes(ShippingDocumentKind.Manifest.toUpperCase());
}
